/**
 * Self-test suite for the encrypted-at-rest thumbnail cache.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/AttachmentThumbnailSelfTest.swift`
 * case-for-case and verifies the nine minimum cases required by the spec.
 *
 * All cases use pure crypto + IndexedDB only — no SolidJS UI, no network.
 * Each store instance uses a unique DB name so cases are isolated.
 *
 * Cases:
 *   Codec (1–3)
 *     1. seal → open round-trip recovers thumbnail bytes.
 *     2. Wrong attachmentId on open: AAD mismatch → GCM auth fails.
 *     3. Wrong bucket on open: AAD mismatch → GCM auth fails.
 *
 *   Integrity (4)
 *     4. SHA-256 integrity check: tampered inner bytes after GCM open are
 *        detected by the embedded digest.
 *
 *   Store (5–8)
 *     5. Tampered ciphertext → GCM auth fails AND row is auto-evicted.
 *     6. Per-(attachmentId, bucket) isolation: writing (A, small) does not
 *        overwrite (A, medium) or (B, small).
 *     7. LRU eviction: total bytes above 200 KB → oldest entry evicted.
 *     8. Persistence: write → reopen store with same DB name → entry
 *        retrievable.
 *
 *   HKDF (9)
 *     9. HKDF determinism: same attachmentKeyBytes derives same cache key
 *        on independent calls.
 *
 *   Extra (10–11)
 *     10. Different attachmentKeyBytes derive different cache keys.
 *     11. LRU preserves newest entry when eviction needed.
 *
 * Registered with the global SelfTestRunner at module load time.
 * Auto-discovered by `selftest/index.ts` via `import.meta.glob("../**\/*SelfTest.ts")`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";

import {
  deriveCacheKey,
  sealThumbnail,
  openThumbnail,
  buildAad,
  ThumbnailCryptoError,
  ThumbnailIntegrityError,
  THUMB_CACHE_KEY_HKDF_INFO,
} from "./AttachmentThumbnail";
import { shouldEvict, THUMB_CACHE_BYTE_LIMIT } from "./AttachmentThumbnailPolicy";
import { AttachmentThumbnailStore } from "./AttachmentThumbnailStore";

// ── Registration ──────────────────────────────────────────────────────────────

SelfTestRunner.register({
  name: "Thumbnails (encrypted-at-rest cache)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // Codec
      await testSealOpenRoundTrip(),
      await testWrongAttachmentIdFails(),
      await testWrongBucketFails(),
      // Integrity
      await testSha256IntegrityCheck(),
      // Store
      await testTamperedCiphertextAutoEvicts(),
      await testPerKeyIsolation(),
      await testLruEviction(),
      await testPersistenceAcrossStoreInstances(),
      // HKDF
      await testHkdfDeterminism(),
      // Extra
      await testDifferentKeysDeriveDistinctCacheKeys(),
      await testLruPreservesNewest(),
    ]),
});

// ── Fixtures ──────────────────────────────────────────────────────────────────

let _seq = 0;

function freshStore(): AttachmentThumbnailStore {
  return new AttachmentThumbnailStore(
    `kaether_thumb_test_${Date.now()}_${_seq++}`,
  );
}

function randomKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

function thumbBytes(content: string): Uint8Array {
  return new TextEncoder().encode(content);
}

// ── Case 1: Codec round-trip ──────────────────────────────────────────────────

async function testSealOpenRoundTrip(): Promise<TestCase> {
  const name = "seal → open round-trip recovers thumbnail bytes";
  try {
    const key = randomKey();
    const cacheKey = await deriveCacheKey(key);
    const tb = thumbBytes("fake-pixel-data-medium-bucket");
    const combined = await sealThumbnail(tb, "attach-1", "medium", cacheKey);
    const recovered = await openThumbnail(combined, "attach-1", "medium", cacheKey);
    const ok =
      recovered.length === tb.length && recovered.every((b, i) => b === tb[i]);
    return ok
      ? pass(name)
      : fail(name, `recovered ${recovered.length} bytes, want ${tb.length}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ── Case 2: Wrong attachmentId → AAD mismatch ─────────────────────────────────

async function testWrongAttachmentIdFails(): Promise<TestCase> {
  const name = "wrong attachmentId on open: AAD mismatch → GCM auth fails";
  try {
    const key = randomKey();
    const cacheKey = await deriveCacheKey(key);
    const tb = thumbBytes("pixels");
    const combined = await sealThumbnail(tb, "attach-correct", "small", cacheKey);
    try {
      await openThumbnail(combined, "attach-WRONG", "small", cacheKey);
      return fail(name, "decryption succeeded with wrong attachmentId");
    } catch (e) {
      if (e instanceof ThumbnailCryptoError) return pass(name);
      return fail(name, `unexpected error type: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `seal failed: ${String(e)}`);
  }
}

// ── Case 3: Wrong bucket → AAD mismatch ──────────────────────────────────────

async function testWrongBucketFails(): Promise<TestCase> {
  const name = "wrong bucket on open: AAD mismatch → GCM auth fails";
  try {
    const key = randomKey();
    const cacheKey = await deriveCacheKey(key);
    const tb = thumbBytes("pixels");
    const combined = await sealThumbnail(tb, "attach-1", "small", cacheKey);
    try {
      await openThumbnail(combined, "attach-1", "large", cacheKey); // wrong bucket
      return fail(name, "decryption succeeded with wrong bucket");
    } catch (e) {
      if (e instanceof ThumbnailCryptoError) return pass(name);
      return fail(name, `unexpected error type: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, `seal failed: ${String(e)}`);
  }
}

// ── Case 4: SHA-256 integrity check ──────────────────────────────────────────

async function testSha256IntegrityCheck(): Promise<TestCase> {
  const name = "SHA-256 integrity: tampered inner bytes detected after GCM open";
  try {
    const key = randomKey();
    const cacheKey = await deriveCacheKey(key);
    const tb = thumbBytes("pixel-data-to-tamper");
    const combined = await sealThumbnail(tb, "attach-sha", "medium", cacheKey);

    // We need to create a payload that passes GCM but has a bad SHA-256 digest.
    // We can do this by constructing a plaintext where the stored digest is wrong,
    // then re-sealing. This tests the integrity path directly.
    const nonce = crypto.getRandomValues(new Uint8Array(12));
    const badDigest = new Uint8Array(32).fill(0xff); // wrong digest
    const badPlaintext = new Uint8Array(32 + tb.length);
    badPlaintext.set(badDigest, 0);
    badPlaintext.set(tb, 32);

    const aad = buildAad("attach-sha", "medium");
    const cryptoKey = await crypto.subtle.importKey(
      "raw", cacheKey, { name: "AES-GCM" }, false, ["encrypt"],
    );
    const ctAndTag = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: "AES-GCM", iv: nonce, tagLength: 128, additionalData: aad },
        cryptoKey,
        badPlaintext,
      ),
    );
    const badCombined = new Uint8Array(12 + ctAndTag.length);
    badCombined.set(nonce, 0);
    badCombined.set(ctAndTag, 12);

    try {
      await openThumbnail(badCombined, "attach-sha", "medium", cacheKey);
      return fail(name, "open succeeded despite bad SHA-256 digest");
    } catch (e) {
      if (e instanceof ThumbnailIntegrityError) return pass(name);
      return fail(name, `expected ThumbnailIntegrityError, got: ${String(e)}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

// ── Case 5: Tampered ciphertext auto-evicts ───────────────────────────────────

async function testTamperedCiphertextAutoEvicts(): Promise<TestCase> {
  const name = "tampered ciphertext → GCM auth fails AND row is auto-evicted";
  try {
    const attachKey = randomKey();
    const store = freshStore();

    // Write a valid entry.
    const tb = thumbBytes("legit-thumbnail");
    await store.put(tb, "attach-tamper", "small", attachKey);

    // Confirm it exists.
    const before = await store.has("attach-tamper", "small");
    if (!before) return fail(name, "entry not found after put");

    // Directly corrupt the stored bytes by writing a bad entry via a second
    // store with the same DB name. We do this by sealing with a different key
    // so the ciphertext is "valid AES-GCM" but AAD-misbound.
    const wrongKey = randomKey();
    const wrongCacheKey = await deriveCacheKey(wrongKey);
    // We need to directly tamper the IDB record. The simplest approach: call
    // put on a second store with the wrong attachmentKeyBytes — that writes a
    // ciphertext that is genuinely sealed but with a different key.
    const store2 = new AttachmentThumbnailStore(
      (store as unknown as { dbName: string }).dbName,
    );
    // We can't easily reach the same DB name — use the store's own put to
    // overwrite with a ciphertext sealed under a different key.
    await store2.put(tb, "attach-tamper", "small", wrongKey);

    // Now try to open with the original correct key — it should fail auth
    // and auto-evict the row.
    try {
      await store.get("attach-tamper", "small", attachKey);
      return fail(name, "open succeeded on wrong-key ciphertext");
    } catch (e) {
      if (!(e instanceof ThumbnailCryptoError)) {
        return fail(name, `expected ThumbnailCryptoError, got: ${String(e)}`);
      }
    }

    // Auto-eviction: the row should now be gone.
    const after = await store.has("attach-tamper", "small");
    return after
      ? fail(name, "corrupted row was not auto-evicted after open failure")
      : pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ── Case 6: Per-(attachmentId, bucket) isolation ──────────────────────────────

async function testPerKeyIsolation(): Promise<TestCase> {
  const name = "per-(attachmentId, bucket) isolation";
  try {
    const keyA = randomKey();
    const keyB = randomKey();
    const store = freshStore();

    const tbASmall  = thumbBytes("A-small");
    const tbAMedium = thumbBytes("A-medium");
    const tbBSmall  = thumbBytes("B-small");

    await store.put(tbASmall,  "A", "small",  keyA);
    await store.put(tbAMedium, "A", "medium", keyA);
    await store.put(tbBSmall,  "B", "small",  keyB);

    const rASmall  = await store.get("A", "small",  keyA);
    const rAMedium = await store.get("A", "medium", keyA);
    const rBSmall  = await store.get("B", "small",  keyB);

    const check = (got: Uint8Array | null, want: Uint8Array, label: string) => {
      if (got == null) return `${label}: null`;
      if (got.length !== want.length || !got.every((b, i) => b === want[i])) {
        return `${label}: bytes mismatch`;
      }
      return null;
    };

    const errs = [
      check(rASmall,  tbASmall,  "(A, small)"),
      check(rAMedium, tbAMedium, "(A, medium)"),
      check(rBSmall,  tbBSmall,  "(B, small)"),
    ].filter(Boolean);

    return errs.length === 0
      ? pass(name)
      : fail(name, errs.join("; "));
  } catch (e) {
    return fail(name, String(e));
  }
}

// ── Case 7: LRU eviction above 200 KB ────────────────────────────────────────

async function testLruEviction(): Promise<TestCase> {
  const name = "LRU policy: byte total above 200 KB → evict oldest";
  try {
    // Use the pure policy function directly (no IndexedDB needed).
    const now = Date.now();
    // Build three entries totalling 190 KB.
    const entries = [
      { attachmentId: "a", bucket: "small" as const, byteLength: 60 * 1024, lastAccessedAt: now - 3000 },
      { attachmentId: "b", bucket: "small" as const, byteLength: 70 * 1024, lastAccessedAt: now - 2000 },
      { attachmentId: "c", bucket: "small" as const, byteLength: 60 * 1024, lastAccessedAt: now - 1000 },
    ];
    // Adding 20 KB would push total to 210 KB > 200 KB.
    const incoming = 20 * 1024;
    const toEvict = shouldEvict(entries, incoming);

    // Should evict "a" (oldest) to bring total from 210 KB down to 150 KB + 20 KB = 170 KB ≤ 200 KB.
    const ok =
      toEvict.length === 1 &&
      toEvict[0]!.attachmentId === "a" &&
      toEvict[0]!.bucket === "small";

    return ok
      ? pass(name)
      : fail(
          name,
          `evict list: ${JSON.stringify(toEvict)}; expected [{a, small}]`,
        );
  } catch (e) {
    return fail(name, String(e));
  }
}

// ── Case 8: Persistence across store instances ────────────────────────────────

async function testPersistenceAcrossStoreInstances(): Promise<TestCase> {
  const name = "persistence: write → reopen DB → entry retrievable";
  try {
    const dbName = `kaether_thumb_persist_${Date.now()}_${_seq++}`;
    const attachKey = randomKey();
    const tb = thumbBytes("persistent-thumbnail");

    const s1 = new AttachmentThumbnailStore(dbName);
    await s1.put(tb, "attach-persist", "large", attachKey);

    // Open a new store instance pointing to the same DB.
    const s2 = new AttachmentThumbnailStore(dbName);
    const recovered = await s2.get("attach-persist", "large", attachKey);

    if (recovered == null) {
      return fail(name, "entry not found on re-opened store");
    }
    const ok = recovered.length === tb.length && recovered.every((b, i) => b === tb[i]);
    return ok ? pass(name) : fail(name, "recovered bytes differ from original");
  } catch (e) {
    return fail(name, String(e));
  }
}

// ── Case 9: HKDF determinism ──────────────────────────────────────────────────

async function testHkdfDeterminism(): Promise<TestCase> {
  const name = `HKDF determinism: same attachmentKeyBytes → same cache key (info: "${THUMB_CACHE_KEY_HKDF_INFO}")`;
  try {
    const attachKey = randomKey();
    const k1 = await deriveCacheKey(attachKey);
    const k2 = await deriveCacheKey(attachKey);
    const ok = k1.length === 32 && k2.length === 32 && k1.every((b, i) => b === k2[i]);
    return ok
      ? pass(name)
      : fail(name, "two derivations from same key produced different output");
  } catch (e) {
    return fail(name, String(e));
  }
}

// ── Case 10: Different keys → different cache keys ────────────────────────────

async function testDifferentKeysDeriveDistinctCacheKeys(): Promise<TestCase> {
  const name = "different attachmentKeyBytes → different derived cache keys";
  try {
    const k1 = await deriveCacheKey(randomKey());
    const k2 = await deriveCacheKey(randomKey());
    // With overwhelming probability two random 32-byte inputs derive different outputs.
    const same = k1.length === k2.length && k1.every((b, i) => b === k2[i]);
    return same
      ? fail(name, "two different root keys derived the same cache key")
      : pass(name);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ── Case 11: LRU preserves newest entry ──────────────────────────────────────

async function testLruPreservesNewest(): Promise<TestCase> {
  const name = "LRU eviction preserves newest entry";
  try {
    const now = Date.now();
    // Fill cache to exactly the byte limit using two entries.
    const entries = [
      { attachmentId: "old", bucket: "small" as const, byteLength: 100 * 1024, lastAccessedAt: now - 5000 },
      { attachmentId: "new", bucket: "small" as const, byteLength: 100 * 1024, lastAccessedAt: now - 100 },
    ];
    // Adding 1 byte pushes total 1 byte over limit → evict "old" only.
    const toEvict = shouldEvict(entries, 1);
    const ok =
      toEvict.length === 1 &&
      toEvict[0]!.attachmentId === "old";
    return ok
      ? pass(name)
      : fail(name, `evict list: ${JSON.stringify(toEvict)}; expected [old]`);
  } catch (e) {
    return fail(name, String(e));
  }
}
